import { TZ } from "./format";

export type ItemState = "date" | "suspended" | "na" | "never" | "expired";
export type Item = {
  id: string; vehicle_id: string; org_id: string; kind: string; label: string; state: ItemState;
  expires_on: string | null; provider: string | null; reference: string | null; notes: string | null;
};
export type Doc = { id: string; vehicle_id: string; item_id: string | null; name: string; mime: string | null; size_bytes: number | null; uploaded_at: string };
export type Vehicle = {
  id: string; org_id: string; device_id: string | null; reg: string; fleet_no: string | null;
  kind: "vehicle" | "heavy" | "vessel" | "trailer" | "other"; make_model: string | null; color: string | null; year: number | null;
  engine_no: string | null; engine_cc: number | null; chassis_vin: string | null; acquired_on: string | null;
  status: "active" | "cancelled"; fuel_card: string | null; project: string | null; notes: string | null;
  specs?: Record<string, string>;
  compliance_items: Item[]; vehicle_documents: Doc[];
};

export const KIND_LABEL: Record<Vehicle["kind"], string> = { vehicle: "Vehicle", heavy: "Heavy equipment", vessel: "Marine vessel", trailer: "Trailer", other: "Other" };
export const MOTOR_KINDS: Vehicle["kind"][] = ["vehicle", "heavy", "trailer", "other"];
export const isVessel = (v: { kind: string }) => v.kind === "vessel";

/** Columns of the compliance board (other items show under "Other"). */
export const BOARD: [string, string][] = [["rego", "LTA Registration"], ["inspection", "LTA Inspection"], ["tpi", "Third-Party Ins."], ["comprehensive", "Comprehensive Ins."], ["permit", "Permit"]];
export const VESSEL_BOARD: [string, string][] = [["msaf_licence", "MSAF Licence"], ["msaf_survey", "MSAF Survey"], ["coastal_trading", "Coastal Trading"], ["hull_insurance", "Hull Insurance"], ["engine_service", "Engine Service"]];

/** Renewal items offered per category; the first ones are added to every new record. */
export const PRESET_ITEMS: [string, string][] = [
  ["rego", "LTA Registration"], ["inspection", "LTA Inspection"], ["tpi", "Third-Party Insurance"], ["comprehensive", "Comprehensive Insurance"],
  ["permit", "AFL AVP Permit"], ["licence", "Operator licence"], ["service", "Service due"],
];
export const VESSEL_ITEMS: [string, string][] = [
  ["msaf_licence", "MSAF Licence"], ["msaf_survey", "MSAF Annual Survey"], ["coastal_trading", "Coastal Trading Licence"],
  ["hull_insurance", "Hull & Machinery Insurance"], ["engine_service", "Engine Service"],
  ["pi_insurance", "Third-Party / P&I Insurance"], ["safety_gear", "Safety equipment (EPIRB, flares, life jackets)"],
  ["slipway", "Slipway / antifouling"], ["skipper_licence", "Skipper licence"], ["mtc", "Marine survey / certificate"],
];
export const DEFAULT_ITEMS = { motor: PRESET_ITEMS.slice(0, 4), vessel: VESSEL_ITEMS.slice(0, 5) };
export const ALL_ITEMS: [string, string][] = [...PRESET_ITEMS, ...VESSEL_ITEMS];

/** Extra details kept in vehicles.specs (free text, max 120 chars each). */
export const VESSEL_SPECS: [string, string, string?][] = [
  ["hull_material", "Hull material", "e.g. Fibreglass, aluminium"], ["hull_id", "Hull ID (HIN)"],
  ["length_m", "Length (m)"], ["beam_m", "Beam (m)"], ["draft_m", "Draft (m)"], ["gross_tonnage", "Gross tonnage"],
  ["engine_count", "Number of engines"], ["engine_make", "Engine make & model", "e.g. 2 × Yamaha F250"], ["engine_hp", "Total horsepower"],
  ["engine_serials", "Engine serial numbers"], ["fuel_type", "Fuel", "Petrol / diesel"], ["fuel_capacity_l", "Fuel capacity (L)"],
  ["passengers", "Passenger capacity"], ["crew", "Crew"], ["home_port", "Home port / mooring"], ["radio_callsign", "Radio call sign"], ["mmsi", "MMSI (AIS)"],
];
export const MOTOR_SPECS: [string, string, string?][] = [["fuel_type", "Fuel", "Petrol / diesel"], ["transmission", "Transmission"], ["seats", "Seats"], ["tyres", "Tyre size"]];

export const STATE_LABEL: Record<ItemState, string> = { date: "Expiry date", suspended: "Suspended", na: "Not applicable", never: "Never issued", expired: "Expired (no date)" };

export type Tier = "expired" | "due" | "ok" | "susp" | "na" | "none";
export const TIER_ORDER: Tier[] = ["expired", "due", "ok", "susp", "na", "none"];

const todayFiji = () => new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(new Date());
export const fmtDay = (iso: string) => new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "2-digit", timeZone: "UTC" }).format(new Date(iso + "T00:00:00Z"));

export function itemStatus(it: Pick<Item, "state" | "expires_on"> | undefined, warnDays: number) {
  if (!it) return { tier: "none" as Tier, text: "—", sub: "", days: 1e9 };
  if (it.state === "suspended") return { tier: "susp" as Tier, text: "Suspended", sub: "", days: 1e9 };
  if (it.state === "na") return { tier: "na" as Tier, text: "N/A", sub: "", days: 1e9 };
  if (it.state === "never") return { tier: "na" as Tier, text: "Never issued", sub: "", days: 1e9 };
  if (it.state === "expired") return { tier: "expired" as Tier, text: "Expired", sub: "renew", days: -1e4 };
  if (!it.expires_on) return { tier: "none" as Tier, text: "—", sub: "no date", days: 1e9 };
  const days = Math.round((Date.parse(it.expires_on) - Date.parse(todayFiji())) / 86400000);
  if (days < 0) return { tier: "expired" as Tier, text: fmtDay(it.expires_on), sub: `${-days}d overdue`, days };
  if (days <= warnDays) return { tier: "due" as Tier, text: fmtDay(it.expires_on), sub: days === 0 ? "today" : `in ${days}d`, days };
  return { tier: "ok" as Tier, text: fmtDay(it.expires_on), sub: `in ${days}d`, days };
}

/** Worst status across a vehicle's items (cancelled vehicles don't count). */
export function worst(v: Vehicle, warnDays: number) {
  let best = { tier: "none" as Tier, text: "No renewals recorded", days: 1e9, label: "" };
  for (const it of v.compliance_items) {
    const s = itemStatus(it, warnDays);
    if (s.tier === "expired" || s.tier === "due") {
      if (best.tier !== "expired" && best.tier !== "due" || s.days < best.days) best = { tier: s.tier, text: `${it.label}: ${s.text} (${s.sub})`, days: s.days, label: it.label };
    } else if (best.tier === "none" && s.tier === "ok") best = { tier: "ok", text: "All current", days: s.days, label: "" };
  }
  return best;
}

export const fmtSize = (n: number | null) => (n == null ? "" : n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);
